import { useCallback, useRef } from "react";

import { StreamingState } from "../stores/StreamingStore";
import { shallow } from "zustand/shallow";
import { useRestoreSubAgents } from "src/reusable/Chat/hooks/useRestoreSubAgents";
import { StoreApi, useStore } from "zustand";
import { useStoreWithEqualityFn } from "zustand/traditional";

export interface ChatStreamSurface extends StreamingState {
  reset: () => void;
  /** dialogueId from streamConfig — set when a brand-new chat is created. */
  configDialogueId?: number;
}

/**
 * Bind a streaming store to React: reactive selectors plus a stable action
 * set. `restoreSubAgents` is owner-only — one fetch per surface, not per card.
 */
export const useStreamBindings = (
  streamStore: StoreApi<StreamingState>,
  opts: { restoreSubAgents?: boolean } = {},
): ChatStreamSurface => {
  // Track both the actions AND the store they were created for
  const stableActionsRef = useRef<{ store: any; actions: any } | null>(null);

  // Update actions when streamStore changes (e.g., different dialogueId).
  // This prevents stale action references pointing to wrong stores.
  if (
    !stableActionsRef.current ||
    stableActionsRef.current.store !== streamStore
  ) {
    const state = streamStore.getState();
    stableActionsRef.current = {
      store: streamStore, // Track which store these actions are for
      actions: {
        // Core actions
        emptySubmission: state.emptySubmission,
        continueConversation: state.continueConversation,
        markTurnActive: state.markTurnActive,
        sendUserMessage: state.sendUserMessage,
        regenerateResponse: state.regenerateResponse,
        resendUserMessage: state.resendUserMessage,
        setAsPreferredMessage: state.setAsPreferredMessage,
        terminateConnection: state.terminateConnection,
        fetchFromSSE: state.fetchFromSSE,
        setDialogue: state.setDialogue,
        setResponseSources: state.setResponseSources,
        setStreamConfig: state.setStreamConfig,
        clearChatErrors: state.clearChatErrors,
        reset: () => {
          state.resetCoreStream();
          state.resetArtifactsStore();
          state.resetParsingState();
        },

        // Token usage for context window tracking
        setTokenUsage: state.setTokenUsage,

        // Reconnection to existing agent streams
        connectToStream: state.connectToStream,
        retryConnection: state.retryConnection,

        // Artifact actions
        addArtifact: state.addArtifact,
        updateArtifactLoading: state.updateArtifactLoading,
        updateArtifactContent: state.updateArtifactContent,
        updateArtifactCrSearchResults: state.updateArtifactCrSearchResults,
        appendToolCallInfoQueue: state.appendToolCallInfoQueue,
        resetToolCallLoadingStates: state.resetToolCallLoadingStates,
        hasArtifact: state.hasArtifact,
        clearArtifactsByMessageId: state.clearArtifactsByMessageId,
        reconstructArtifactsFromMessages:
          state.reconstructArtifactsFromMessages,

        // Message queue actions
        queueMessage: state.queueMessage,
        removeQueuedMessage: state.removeQueuedMessage,
        processMessageQueue: state.processMessageQueue,
        clearMessageQueue: state.clearMessageQueue,

        // Restore sub-agent cards for a loaded dialogue
        hydrateSubAgents: state.hydrateSubAgents,
      },
    };
  }

  const dialogue = useStore(streamStore, (state) => state.dialogue);
  const artifacts = useStore(streamStore, (state) => state.artifacts);
  const responseSources = useStore(
    streamStore,
    (state) => state.responseSources,
  );
  const toolCallInfoQueue = useStore(
    streamStore,
    (state) => state.toolCallInfoQueue,
  );
  const status = useStoreWithEqualityFn(
    streamStore,
    (state) => ({
      isLoading: state.isLoading,
      isConnected: state.isConnected,
      streamError: state.streamError,
      isConnectionError: state.isConnectionError,
      isEnded: state.isEnded,
      isMessageStreamStopped: state.isMessageStreamStopped,
      isTurnActive: state.isTurnActive,
      errorMetadata: state.errorMetadata,
    }),
    shallow,
  );

  // Select tokenUsage separately to ensure re-renders on updates during streaming
  const tokenUsage = useStore(streamStore, (state) => state.tokenUsage);

  // Expose lastDialogueId so callers can gate retry button visibility —
  // retryConnection is a no-op when no dialogue ID was ever established.
  const lastDialogueId = useStore(streamStore, (state) => state.lastDialogueId);

  // Expose the dialogue id from streamConfig. Unlike lastDialogueId (only set
  // on resume/reconnect), this reflects the id assigned to a brand-new chat
  // when the first /chat/start metadata event lands, so callers can sync the
  // URL / enable sharing for a freshly created dialogue.
  const configDialogueId = useStore(
    streamStore,
    (state) => state.streamConfig?.dialogueId,
  );

  // Sub-agent activity state for inline tracking (one entry per delegated agent)
  const activeSubAgents = useStore(
    streamStore,
    (state) => state.activeSubAgents,
  );

  // Restore persisted sub-agent cards for a resumed dialogue (they don't
  // replay over SSE). Owner surfaces opt in; descendants must not re-fetch.
  const hydrateSubAgentsStable = useCallback(
    (runs: Parameters<StreamingState["hydrateSubAgents"]>[0]) =>
      streamStore.getState().hydrateSubAgents(runs),
    [streamStore],
  );
  useRestoreSubAgents(
    configDialogueId ?? lastDialogueId,
    hydrateSubAgentsStable,
    opts.restoreSubAgents ?? false,
  );

  // Message queue state for staged messages
  const pendingMessageQueue = useStore(
    streamStore,
    (state) => state.pendingMessageQueue,
  );

  /*
   * Reactive Hooks
   * reactive getter function that needs the most up to date artifact state
   * hook needs to handle reactivity since we are abstracting the store
   */
  const getArtifactsByMessageId = useCallback(
    (messageId: string | null | undefined) => {
      if (!messageId) return [];
      return streamStore.getState().getArtifactsByMessageId(messageId);
    },
    [artifacts, streamStore],
  );

  const getToolCallInfoQueueLength = useCallback(() => {
    return streamStore.getState().getToolCallInfoQueueLength();
  }, [artifacts, toolCallInfoQueue, streamStore]);

  const popToolCallInfoQueue = useCallback(() => {
    return streamStore.getState().popToolCallInfoQueue();
  }, [artifacts, streamStore]);

  const findLoadingArtifactByToolName = useCallback(
    (messageId: string | null | undefined, toolName: string) => {
      if (!messageId) return undefined;
      return streamStore
        .getState()
        .findLoadingArtifactByToolName(messageId, toolName);
    },
    [artifacts, streamStore],
  );

  return {
    dialogue,
    artifacts,
    responseSources,
    ...status,
    tokenUsage,
    lastDialogueId,
    configDialogueId,
    activeSubAgents,
    pendingMessageQueue,
    getArtifactsByMessageId,
    getToolCallInfoQueueLength,
    popToolCallInfoQueue,
    findLoadingArtifactByToolName,

    ...stableActionsRef.current?.actions,
  };
};
